import { useCallback, useEffect } from "react";
import * as notify from "../../../../../shared/notify";
import { newTemplate, oldTemplate } from "../lib/templates";
import { useKeyboardShortcut } from "../../../../../shared/hooks/hotkeys";
import { sendSingleAttributeData } from "../../../lib/attribute-data";
import { nutrientValidation } from "../lib/helpers";
import { useNutritionValidator } from "../../../../../shared/hooks/api/use-nutrition-validator";
import { useNutritionPanelState } from "./use-nutrition-panel-state";
import { useTemplateVersion } from "./use-template-version";

export const useNutritionPanels = () => {
    const { mutate: validatePanels, isLoading } = useNutritionValidator();
    const hotkeys = useKeyboardShortcut();
    const { state, dispatch, panelInfo } = useNutritionPanelState();
    const { isNewTemplateUsed, setNewTemplate } = useTemplateVersion();

    useEffect(() => {
        switch (hotkeys) {
            case "SAVE":
                savePanel();
                break;
            default:
                break;
        }
    }, [hotkeys]);

    const checkEmptyValues = () => {
        let validNutrients = true;
        state.panels.forEach((panel) => {
            if (!nutrientValidation(panel.columns[0].dietary.facts) || !nutrientValidation(panel.columns[0].vitamineral.facts)) {
                validNutrients = false;
                notify.error("Nutrient can not be blank");
            }
        });

        return validNutrients;
    };

    const filterDqParameters = (panels) => {
        return panels.map((panel) => {
            return {
                ...panel,
                columns: panel.columns.map((column) => {
                    const {
                        calorie: { dataQualityIssue, ...calorieParams },
                    } = column;

                    return {
                        ...column,
                        calorie: {
                            ...calorieParams,
                        },
                        dietary: {
                            ...column.dietary,
                            facts: column.dietary.facts.map((factItem) => {
                                const {
                                    nutrient: { dataQualityIssue: nutrientDataQualityIssue, ...nutrientParams },
                                } = factItem;

                                return {
                                    ...factItem,
                                    nutrient: {
                                        ...nutrientParams,
                                    },
                                };
                            }),
                        },
                    };
                }),
            };
        });
    };

    const savePanel = async () => {
        // Validation
        if (!checkEmptyValues()) {
            return false;
        }

        dispatch({ type: "CLEAR_VALIDATION_WARNINGS" });
        validatePanels(
            {
                uuid: panelInfo.attribute.uuid,
                attribute: {
                    panels: filterDqParameters(state.panels),
                },
            },
            {
                onSuccess: async ({ hasDqErrors, attributeValueWithDqErrors, globalDqErrors }) => {
                    if (hasDqErrors) {
                        dispatch({
                            type: "SET_VALIDATION_WARNINGS",
                            panels: attributeValueWithDqErrors[panelInfo.attribute.uuid].panels,
                            globalDqErrors,
                        });

                        const confirm = await notify.confirm(
                            `Please review values in orange against package label. If correct, click "Yes" to continue.`,
                            { yesLabel: "Yes", noLabel: "No" }
                        );

                        if (!confirm) {
                            return;
                        }
                    }

                    try {
                        if (!panelInfo.skipInternalSave) {
                            const response = await sendSingleAttributeData(panelInfo.codingWipKey, panelInfo.taskId, panelInfo.sourcePage, {
                                definition: panelInfo.attribute.uuid,
                                type: panelInfo.attribute.componentType,
                                value: { panels: filterDqParameters(state.panels) },
                            });

                            if (!response) {
                                return false;
                            }

                            notify.success("Saved");
                        }

                        panelInfo.updateOnChange(
                            panelInfo.attribute.uuid,
                            { panels: filterDqParameters(state.panels) },
                            panelInfo.attribute.componentType
                        );
                        dispatch({ type: "SET_DIRTY_FLAG", isDirty: false });

                        return true;
                    } catch (err) {
                        notify.error(err);

                        return false;
                    }
                },
            }
        );
    };

    const changeTab = (tabIndex) => {
        dispatch({ type: "SET_PANEL_INDEX", selectedPanel: tabIndex });
    };

    const addPanel = async () => {
        const isNewTemplateSelected = await notify.confirm("Use 'new' template?");
        dispatch({ type: "ADD_PANEL", template: isNewTemplateSelected ? newTemplate() : oldTemplate() });
        setNewTemplate(isNewTemplateSelected);
    };

    const removePanel = async () => {
        const verify = await notify.confirm(`Are you sure you want to delete Panel ${state.selectedPanel + 1}?`);

        if (verify) {
            dispatch({ type: "REMOVE_PANEL" });
        }
    };

    const updateField = (key, value, column = false) => {
        dispatch({ type: "UPDATE_STATE", key: key, value: value, column: column });
    };

    const updateFieldDeep = (section, key, value, column = false) => {
        dispatch({ type: "UPDATE_DEEP_STATE", section: section, key: key, value: value, column: column });
    };
    const addFact = (section) => {
        dispatch({ type: "ADD_FACT", section: section });
    };
    const updateFacts = (key, value, column = false, skipReorder) => {
        dispatch({ type: "UPDATE_FACTS", key: key, value: value, column: column, isNewTemplateUsed, skipReorder });
    };

    const addColumn = () => {
        dispatch({ type: "CLONE_COLUMN" });
    };
    const removeColumn = async (columnIndex) => {
        const verify = await notify.confirm(`Are you sure you want to delete As Prepared ${columnIndex + 1}?`);

        if (verify) {
            dispatch({ type: "REMOVE_COLUMN", columnIndex });
        }
    };

    const handleResetDefaultNutritionValues = useCallback(async () => {
        const confirm = await notify.confirm("Are you sure you want to clear all Nutrient values? This action cannot be undone.", {
            yesLabel: "Yes",
            noLabel: "No",
        });

        if (!confirm) {
            return;
        }

        dispatch({ type: "RESET_DEFAULT_NUTRITION_VALUES" });
    }, [isNewTemplateUsed]);

    const handleResetDefaultVitaminsValues = useCallback(async () => {
        const confirm = await notify.confirm("Are you sure you want to remove all Vitamin/Mineral rows? This action cannot be undone.", {
            yesLabel: "Yes",
            noLabel: "No",
        });

        if (!confirm) {
            return;
        }

        dispatch({ type: "RESET_DEFAULT_VITAMINS_VALUES" });
    }, [isNewTemplateUsed]);

    return {
        savePanel,
        addPanel,
        changeTab,
        removePanel,
        updateField,
        updateFieldDeep,
        addFact,
        updateFacts,
        handleResetDefaultVitaminsValues,
        handleResetDefaultNutritionValues,
        removeColumn,
        addColumn,
        isValidationProcessing: isLoading,
        globalDqErrors: state.globalDqErrors,
    };
};
